/**
 * Use this file for helper funcs to build the various different requests
 * to Orchestration Service for different flows - INN, OON, GAC Flow, etc.
 */

import {
    COVERAGE_TYPE,
    CARE_PATH_CATEGORY_CODE,
    COVERAGE_TYPE_CODE,
    DEFAULT_TOOTH_RANGE,
    ORCHESTRATION_SERVICE_ERROR_CODES,
    ERROR_CODE_HEADER,
    ERROR_MESSAGE_HEADER,
    OON_NO_RATES_DISCLAIMER_ID,
} from '../constants/index';

export const determineFormattedCoverageType = (coverageTypeForSwitch) => {
    let formattedCoverageType;
    // this switch is written this way in case other types arise (i.e. physical, dental, pharm, etc.)
    switch (coverageTypeForSwitch) {
        case CARE_PATH_CATEGORY_CODE.MEDICAL:
            formattedCoverageType = COVERAGE_TYPE.MEDICAL;
            break;
        case CARE_PATH_CATEGORY_CODE.BEHAVIORAL:
            formattedCoverageType = COVERAGE_TYPE.BEHAVIORAL;
            break;
        case CARE_PATH_CATEGORY_CODE.DENTAL:
            formattedCoverageType = COVERAGE_TYPE.DENTAL;
            break;
        case CARE_PATH_CATEGORY_CODE.VISION:
            formattedCoverageType = COVERAGE_TYPE.VISION;
            break;
        default:
            formattedCoverageType = COVERAGE_TYPE.MEDICAL;
            break;
    }

    return formattedCoverageType;
};

/**
 * Take the coverage type that matches the care path category for procedure the member is searching for
 * to find the corresponding productCode and eligibilityIdentifier.
 */
export const determineMemberFieldsBasedOnCoverage = (memberDetails, carePathCategoryCode) => {
    // Default productCode initially set, productCode is used in the Disclaimer service
    let productCode = '';
    let eligibilityIdentifier;

    let benSrvcCovType = carePathCategoryCode;

    // Physical health is denoted as a M service but coverageTypeCode = OPH from Benefit service response
    if (memberDetails?.sourceCode == 'PH') {
        benSrvcCovType = COVERAGE_TYPE_CODE.OPH;
    }

    memberDetails?.eligibility.forEach((eligibility) => {
        eligibility.memberHealthCoverage.coverageType.forEach((coverage) => {
            if (benSrvcCovType == coverage.typeCode.coverageTypeCode && coverage?.productCode) {
                productCode = coverage.productCode;
                eligibilityIdentifier = coverage.eligibilityIdentifier;
            }
        });
    });

    return { productCode: productCode, eligibilityIdentifier: eligibilityIdentifier };
};

export const getProviderIdentifiers = (provider) => {
    const npiValue = provider?.locAndPractRole?.[0]?.identifiers?.nationalProviderId || null;

    const piddValue = provider?.locAndPractRole?.[0]?.identifiers?.providerDigestId || null;

    const mpinValue = provider?.locAndPractRole?.[0]?.identifiers?.mpin || null;

    const pcpidValue = provider?.locAndPractRole?.[0]?.identifiers?.pcpId || null;

    const tinValue = provider?.locAndPractRole?.[0]?.identifiers?.taxIdData || null;

    // practiceId is relevant for dental providers
    const practiceIdValue = provider?.locAndPractRole?.[0]?.identifiers?.practiceId || null;

    return { npiValue, piddValue, mpinValue, pcpidValue, tinValue, practiceIdValue };
};

export const getDentalAuxData = (matchedProcedure) => {
    const lineItem = matchedProcedure?.lineItem;

    const toothRangeFromCarePath =
        (lineItem?.toothRangeAdult ?? lineItem?.toothRangeChild) ||
        null; /* in most cases, this should be an unsorted array of numbers (adult) or letters (child) */
    const toothPositionFromCarePath =
        (lineItem?.toothPositionAdult ?? lineItem?.toothPositionChild) ||
        null; /* this is usually a number (adult) or a letter (child) */

    const dentalAuxData = {
        beginToothNum: DEFAULT_TOOTH_RANGE.beginPosition,
        endToothNum: DEFAULT_TOOTH_RANGE.endPosition,
        dentalUnits: lineItem?.quantity ?? '1',
    };

    /* 
        As of 1/8/24:
            - there appears to be no difference between using the correct range vs position, forwards or backwards
            - we will fallback to using from the range if position is not set
            - toothRangeAdult looks like: [5, 10, 14, 1, 6] which would indicate [[1], [5,6], [10], [14]] 
            as possible valid ranges according to carePaths team
    */
    if (toothRangeFromCarePath) {
        dentalAuxData.beginToothNum =
            (toothPositionFromCarePath ?? toothRangeFromCarePath?.[0]) ||
            DEFAULT_TOOTH_RANGE.beginPosition;
        dentalAuxData.endToothNum =
            (toothPositionFromCarePath ?? toothRangeFromCarePath?.[0]) ||
            DEFAULT_TOOTH_RANGE.beginPosition;
    }

    return dentalAuxData;
};

/**
 * Takes in procedure object for standard OON request, and adds fields necessary for pre-d dental oon request
 */
export const buildDentalOonRequest = (proc, matchedProcedure) => {
    proc.procedureCode = {
        ...proc.procedureCode,
        dentalAuxData: getDentalAuxData(matchedProcedure),
    };

    return proc;
};

export const getBillingType = (selectedServiceObj) => {
    return selectedServiceObj?.carePathStep?.category == 'Inpatient' ? 'Inpatient' : 'Outpatient';
};

/**
 * When Orchestration Service returns with a 206 Partial Content Status, determine what error occured based on the response headers
 * Log error message to console accordingly
 * @param {object} headers Orch Service response headers that include x-epe-error-code header, a comma delimited list of error codes
 * @returns
 * @todo Add handling for any other new orchestration partial response statuses beyond GAC
 */
export const determinePartialContentError = (headers) => {
    try {
        const errorCodes = headers?.[ERROR_CODE_HEADER];
        const errorMessages = headers?.[ERROR_MESSAGE_HEADER];
        let errorMessage;
        if (errorCodes) {
            const errorCodesArray = errorCodes.split(', ');
            const errorMessagesArray = errorMessages?.split(', ');
            ORCHESTRATION_SERVICE_ERROR_CODES.GAC_NOT_AVAILABLE.code.forEach((code) => {
                if (errorCodesArray?.includes(code)) {
                    const errorIndex = errorCodesArray.indexOf(code);
                    errorMessage =
                        ORCHESTRATION_SERVICE_ERROR_CODES.GAC_NOT_AVAILABLE.displayMessage;
                    console.error(`${errorMessage}: ${errorMessagesArray?.[errorIndex]}`);
                }
            });
        }
    } catch (err) {
        console.error(err);
    }
};

export const checkForNoOONRatesDisclaimer = (resolve, resp) => {
    let noOonRates = false;
    const oonClaimsDisclaimer = resp?.response?.data?.scenarios?.[0]?.serviceNotes?.find((note) => {
        return note?.id == OON_NO_RATES_DISCLAIMER_ID;
    });
    if (oonClaimsDisclaimer) {
        noOonRates = true;
    } else {
        // backup plan incase we failed to get disclaimers - probably can get rid of this eventually
        const errorCodes = resp.response.headers?.[ERROR_CODE_HEADER] ?? '';
        const errorCodesArray = errorCodes.split(', ') ?? [];
        ORCHESTRATION_SERVICE_ERROR_CODES.NO_OON_RATES.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                noOonRates = true;
            }
        });
    }

    if (noOonRates) {
        resp.response.data.noOONRates = oonClaimsDisclaimer?.text;
        resp.response.data.scenarios = [];
        resolve(resp.response.data);
    }
};

export const determineIndemnityNoRatesResponse = (resp, memberDetails) => {
    const headers = resp.response.headers;
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    let indemnityNoResponse = false;

    if (errorCodes) {
        const errorCodesArray = errorCodes.split(', ');
        errorCodesArray.forEach((code) => {
            if (
                ORCHESTRATION_SERVICE_ERROR_CODES.NO_OON_RATES_OR_BENEFITS.code.includes(code) ||
                ORCHESTRATION_SERVICE_ERROR_CODES.NO_OON_RATES.code.includes(code)
            ) {
                indemnityNoResponse = true;
            }
        });
    }

    const coverageTypeFromResponse = resp?.response?.data?.scenarios?.[0]?.coverageType;
    const coverageTypesArray = memberDetails?.eligibility?.[0]?.memberHealthCoverage?.coverageType;
    const matchingCoverageobject = getCoverageObject(coverageTypesArray, coverageTypeFromResponse);
    const isIndemnity = matchingCoverageobject?.indemnityFlag;
    const isINNEstimate =
        resp?.response?.data?.scenarios?.[0]?.claims?.[0]?.procedures?.[0]?.provider?.isInNetwork;

    if (indemnityNoResponse && isIndemnity && isINNEstimate) {
        return true;
    }
    return false;
};

const getCoverageObject = (coverageTypesArray, coverageTypeFromResponse) => {
    return coverageTypesArray?.find((coverageObject) => {
        return (
            coverageObject?.typeCode?.coverageTypeCode?.substring(0, 1) ==
            coverageTypeFromResponse?.substring(0, 1)?.toUpperCase()
        );
    });
};

export const determineNoOONBenefits = (resp) => {
    const headers = resp.response.headers;
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    const errorMessages = headers?.[ERROR_MESSAGE_HEADER];
    let errorMessage;
    let hasNoOonBenefits = false;
    let noOonRatesDisclaimer = resp?.response?.data?.scenarios?.[0]?.serviceNotes?.find((note) => {
        return note?.id == OON_NO_RATES_DISCLAIMER_ID;
    });
    if (errorCodes) {
        const errorCodesArray = errorCodes.split(', ');
        const errorMessagesArray = errorMessages?.split(', ');
        ORCHESTRATION_SERVICE_ERROR_CODES.NO_OON_RATES_OR_BENEFITS.code.forEach((code) => {
            if (errorCodesArray?.includes(code) && noOonRatesDisclaimer) {
                const errorIndex = errorCodesArray.indexOf(code);
                errorMessage =
                    ORCHESTRATION_SERVICE_ERROR_CODES.NO_OON_RATES_OR_BENEFITS.displayMessage;
                console.error(`${errorMessage}: ${errorMessagesArray?.[errorIndex]}`);
                hasNoOonBenefits = true;
            }
        });
    }
    return { hasNoOonBenefits, noOonRatesDisclaimer };
};

export const determineNoGACPACRates = (headers) => {
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    const errorMessages = headers?.[ERROR_MESSAGE_HEADER];
    let errorMessage;
    let hasNoGACPACRates = false;
    let hasBenefits;
    if (errorCodes) {
        const errorCodesArray = errorCodes.split(', ');
        const errorMessagesArray = errorMessages?.split(', ');
        ORCHESTRATION_SERVICE_ERROR_CODES.NO_GAC_OR_PAC_RATES.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                const errorIndex = errorCodesArray.indexOf(code);
                errorMessage = ORCHESTRATION_SERVICE_ERROR_CODES.NO_GAC_OR_PAC_RATES.displayMessage;
                console.error(`${errorMessage}: ${errorMessagesArray?.[errorIndex]}`);
                hasBenefits = code === 'OR17' ? true : false;
                hasNoGACPACRates = true;
            }
        });
    }
    return { hasNoGACPACRates, errorMessage, hasBenefits };
};
